import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import IntlTelInput from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DollarCircleOutlined,
  LoadingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useUser } from "@clerk/nextjs";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox, ConfigProvider, Divider, Input, Modal } from "antd";
import { z } from "zod";

import Avatar from "@/components/atoms/avatar";
import { Button } from "@/components/atoms/button/button";
import LottiePlayer from "@/components/atoms/lottie-player";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/utils/form";
import useClientBackend from "@/hooks/useBackend/useClientBackend";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { fullNameRegex } from "@/utils/regex";
import type { Push } from "@/utils/types";

export const hireThisProfessionalFormSchema = z.object({
  fullName: z
    .string({ required_error: "Este campo é obrigatório" })
    .min(
      3,
      "Não utilize abreviações. Este campo deve conter pelo menos duas palavras (Ex.: Nome Sobrenome).",
    )
    .trim()
    .max(120, "Nome muito longo.")
    .regex(
      fullNameRegex,
      "Não utilize abreviações. Este campo deve conter pelo menos duas palavras (Ex.: Nome Sobrenome).",
    ),
  email: z
    .string({ required_error: "Este campo é obrigatório" })
    .max(
      320,
      "E-mail inválido, por favor insira um e-mail conforme o exemplo a seguir: email@corporativo.com.br",
    )
    .trim()
    .email(
      "E-mail inválido, por favor insira um e-mail conforme o exemplo a seguir: email@corporativo.com.br",
    )
    .refine(
      (value) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/;
        return emailRegex.test(value);
      },
      {
        message:
          "E-mail inválido, por favor insira um e-mail conforme o exemplo a seguir: email@corporativo.com.br",
      },
    ),
  phoneNumber: z
    .string({ required_error: "Este campo é obrigatório" })
    .refine(
      (phone) => phone.replace(/\D/g, ""),
      "Verifique que o número está completo e inclua o DDD da sua região.",
    ),
});

export const confirmationSchema = z.object({
  optIn: z.object({
    email: z.boolean().refine((value) => value === true),
    consent: z.boolean().refine((value) => value === true),
  }),
});

export type HireThisProfessionalFormData = z.infer<
  typeof hireThisProfessionalFormSchema
>;
export type ConfirmationFormData = z.infer<typeof confirmationSchema>;

type FormSteps = "linkedInAndPhone" | "confirmation" | "error" | "success";

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function HireThisProfessionalModal({ open, setOpen }: Props) {
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<FormSteps>("linkedInAndPhone");
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState(5);
  const router = useRouter();
  const pathname = usePathname();
  const { user, isLoaded } = useUser();

  const [value, copy] = useCopyToClipboard(3000);

  const [storageData, setStorageData] = useLocalStorage<Push>(
    "push",
    {} as Push,
  );

  const { postHireNewTalent } = useClientBackend();

  const hireThisProfessionalForm = useForm<HireThisProfessionalFormData>({
    defaultValues: {
      fullName: storageData?.fullName ?? "",
      email: storageData?.email ?? "",
      phoneNumber: storageData?.phoneNumber ?? "",
    },
    resolver: zodResolver(hireThisProfessionalFormSchema),
  });

  const confirmationForm = useForm<ConfirmationFormData>({
    defaultValues: {
      optIn: {
        email: storageData?.optIn?.email ?? false,
        consent: storageData?.optIn?.consent ?? false,
      },
    },
    resolver: zodResolver(confirmationSchema),
  });

  const linkedInAndPhoneHandleSubmit: SubmitHandler<
    HireThisProfessionalFormData
  > = (data) => {
    if (!phoneIsValid) {
      return hireThisProfessionalForm.setError("phoneNumber", {
        type: "manual",
        message:
          "Verifique que o número está completo e inclua o DDD da sua região.",
      });
    }
    if (!hireThisProfessionalFormSchema.safeParse(data)) return;
    setStorageData({ ...storageData, ...data });
    setCurrentStep("confirmation");
  };

  const confirmationHandleSubmit: SubmitHandler<ConfirmationFormData> = (
    data,
  ) => {
    const newData = { ...storageData, ...data };
    setStorageData(newData);

    if (process.env.NODE_ENV === "production") {
      postHireNewTalent({
        fullName: newData.fullName,
        email: newData.email,
        phoneNumber: newData.phoneNumber,
        optIn: newData.optIn,
        jobId: newData.jobId,
        candidate: newData.candidate,
      })
        .then(() => {
          setCurrentStep("success");
        })
        .catch((error) => {
          console.error(error);
          setCurrentStep("error");
        });
    } else {
      setCurrentStep("success");
    }
  };

  useEffect(() => {
    if (open && user) {
      setCurrentStep("confirmation");
      setStorageData({
        ...storageData,
        fullName: user?.fullName,
        email: user?.emailAddresses[0]!.emailAddress,
        optIn: {
          consent: true,
          email: true,
        },
      } as Push);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isLoaded, user]);

  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (currentStep === "success") {
      timerId = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);

      if (timeLeft <= 0) {
        clearInterval(timerId);
        router.push(`/job/${storageData.jobId}/perfis/conclusao`);
      }
    }

    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [timeLeft, currentStep, pathname, router, storageData.jobId]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Modal: {
            zIndexPopupBase: 1001,
          },
        },
      }}
    >
      <Modal
        open={open}
        onCancel={() => {
          if (currentStep === "success") return;
          setOpen(false);
          setShowTerms(false);
          setCurrentStep("linkedInAndPhone");
        }}
        closable={currentStep !== "success"}
        footer={null}
        width={600}
        centered={typeof window !== "undefined" && window.innerWidth < 600}
        title={
          currentStep !== "success" ? (
            <div
              className="flex cursor-pointer flex-row gap-4"
              onClick={() => {
                if (showTerms) return setShowTerms(false);
                if (currentStep === "linkedInAndPhone") setOpen(false);
                else setCurrentStep("linkedInAndPhone");
              }}
            >
              <ArrowLeftOutlined className="text-base text-aubergine-perl-600" />
              <p className="font-normal">Voltar</p>
            </div>
          ) : undefined
        }
        closeIcon={
          <CloseCircleOutlined className="text-2xl text-aubergine-perl-600" />
        }
      >
        {currentStep === "linkedInAndPhone" && (
          <Form {...hireThisProfessionalForm}>
            <h2 className="mb-4 mt-2 text-2xl font-medium">
              Contrate este profissional
            </h2>
            <p className="mb-4 text-base font-normal sm:text-xl">
              Insira os dados abaixo e nossa equipe te ajudará a entrar em
              contato com o perfil selecionado.
            </p>
            <form
              onSubmit={hireThisProfessionalForm.handleSubmit(
                linkedInAndPhoneHandleSubmit,
              )}
              className="flex flex-col gap-6"
            >
              <FormField
                control={hireThisProfessionalForm.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="fullName">
                      <span className="text-aubergine-perl-600">*</span> Seu
                      nome
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        id="fullName"
                        className="w-full"
                        placeholder="Nome completo"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={hireThisProfessionalForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="email">
                      <span className="text-aubergine-perl-600">*</span> E-mail
                      corporativo
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        id="email"
                        className="w-full"
                        placeholder="email@corporativo.com.br"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={hireThisProfessionalForm.control}
                name="phoneNumber"
                render={() => (
                  <FormItem>
                    <FormLabel htmlFor="phoneNumber">
                      <span className="text-aubergine-perl-600">*</span> Número
                      de celular (WhatsApp)
                    </FormLabel>
                    <FormControl>
                      <IntlTelInput
                        fieldId="phoneNumber"
                        fieldName="phoneNumber"
                        containerClassName="intl-tel-input w-full mt-2 [&>.flag-container>.selected-flag]:rounded-tl-2xl [&>.flag-container>.selected-flag]:rounded-bl-2xl [&>.flag-container>.selected-flag]:w-[4.25rem] [&>.flag-container>.selected-flag]:border-r-[1px] [&>.flag-container>.selected-flag]:border-zinc-300 [&>.flag-container>.selected-flag]:border-solid [&>.flag-container>.selected-flag]:border-0"
                        inputClassName="relative flex items-center pl-20 focus-visible:outline-none transition-all disabled:pointer-events-none h-10 text-sm bg-transparent border border-zinc-400/60 hover:border-brand-primary placeholder:text-zinc-400/60 hover:placeholder:text-zinc-400 w-full border-solid rounded-2xl"
                        preferredCountries={["br", "us"]}
                        format
                        value={hireThisProfessionalForm.getValues(
                          "phoneNumber",
                        )}
                        onPhoneNumberChange={(
                          isValid,
                          value,
                          _selectedCountry,
                          _fullNumber,
                        ) => {
                          hireThisProfessionalForm.setValue(
                            "phoneNumber",
                            value,
                          );
                          setPhoneIsValid(isValid);
                        }}
                        ref={
                          hireThisProfessionalForm.register("phoneNumber").ref
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full"
                color="primary"
                disabled={!isLoaded}
              >
                Próxima etapa
              </Button>
            </form>
          </Form>
        )}
        {currentStep === "confirmation" && (
          <Form {...confirmationForm}>
            <form
              onSubmit={confirmationForm.handleSubmit(confirmationHandleSubmit)}
            >
              <h2 className="mb-4 mt-2 text-2xl font-medium">
                {!showTerms && "Contrate este profissional"}
              </h2>
              <div className="grid gap-4">
                {!showTerms ? (
                  user ? (
                    <>
                      <div className="my-4 flex items-center gap-2 rounded-lg p-4 shadow-basic">
                        <Avatar
                          size="md"
                          src={storageData.candidate.profilePicUrl}
                          alt={storageData.candidate.key}
                          className="col-span-1"
                        />
                        <div className="flex flex-col gap-1">
                          <span className="text-xl font-medium">
                            Perfil {storageData.candidate.key.split("_")[1]}
                          </span>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                            <div className="flex items-center gap-2 text-sm lg:text-base">
                              <UserOutlined className="text-aubergine-perl-500" />
                              <p className="leading-4">
                                Nivel {storageData.candidate.level}
                              </p>
                            </div>

                            <div className="flex items-center gap-2 text-sm lg:text-base">
                              <DollarCircleOutlined className="text-aubergine-perl-500" />
                              <p className="leading-4">
                                Pretensão:{" "}
                                {storageData.candidate.salaryExpectation}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col gap-4 break-all rounded-md bg-dr-white-600 p-4 text-sm">
                      <p>
                        <strong>Nome:</strong>
                        {` ${storageData.fullName}`}
                      </p>
                      <p>
                        <strong>Email:</strong>
                        {` ${storageData.email}`}
                      </p>

                      {storageData.phoneNumber && (
                        <p>
                          <strong>Celular:</strong>
                          {` ${storageData.phoneNumber}`}
                        </p>
                      )}
                    </div>
                  )
                ) : (
                  <div className="-mt-4 flex flex-col">
                    <Divider className="my-2" />
                    <p className="scrollbar h-[300px] overflow-y-auto overflow-x-hidden pr-4 text-justify text-base">
                      Nós, da HARPIO, buscando aproximar o seu talento notável a
                      empresas incríveis, ficamos muito felizes em ter você como
                      interessado em participar dos Processos Seletivos que
                      conduzimos e precisaremos coletar alguns dados.
                      <br />
                      <br />
                      Assim, solicitamos que você concorde em disponibilizar
                      algumas informações contidas em sua identificação e demais
                      formulários que possam vir a ser preenchidos ou fornecidos
                      durante toda a sua jornada conosco.
                      <br />
                      <br />
                      A HARPIO se compromete a utilizar apenas os dados
                      essenciais para a comunicação entre as partes; respeitando
                      a Lei Geral de Proteção de Dados Pessoais (LGPD). <br />
                      <br />
                      Ressaltamos que os dados informados são de
                      responsabilidade dos candidatos.
                      <br />
                      <br />
                      O seu currículo poderá ser salvo eletronicamente em
                      ambiente virtual seguro e mantido sob nossa guarda pelo
                      prazo máximo de 02 (dois) anos após o término do processo
                      seletivo mais recente que você tenha participado.
                      <br />
                      <br />
                      Ainda, em conformidade com a LGPD, você poderá solicitar a
                      exclusão, bem como sanar dúvidas, obter mais informações
                      ou outras solicitações referentes a seus dados, através do
                      e-mail dpo@harpio.com.br.
                      <br />
                      <br />
                      Desta forma, caso não haja manifestação contrária,
                      consideraremos a sua concordância a este termo de
                      consentimento.
                    </p>
                    <Divider className="my-2" />
                  </div>
                )}
                {!user && (
                  <>
                    <div className="grid gap-2">
                      <FormField
                        control={confirmationForm.control}
                        name="optIn.email"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <div className="flex items-center gap-1 space-x-2">
                                <Checkbox
                                  id="optIn.email"
                                  checked={!!field.value}
                                  onChange={(checked) =>
                                    field.onChange(checked.target.checked)
                                  }
                                />
                                <FormLabel
                                  htmlFor="optIn.email"
                                  className="cursor-pointer font-roboto text-base font-normal"
                                >
                                  Estou de acordo com o{" "}
                                  <span
                                    className="cursor-pointer text-aubergine-perl-600 hover:underline"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setShowTerms((old) => !old);
                                    }}
                                  >
                                    Termo de Consentimento
                                  </span>{" "}
                                  para preencher e disponibilizar meus dados.
                                </FormLabel>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid gap-2">
                      <FormField
                        control={confirmationForm.control}
                        name="optIn.consent"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <div className="flex items-center gap-1 space-x-2">
                                <Checkbox
                                  id="optIn.consent"
                                  checked={!!field.value}
                                  onChange={(checked) =>
                                    field.onChange(checked.target.checked)
                                  }
                                />
                                <FormLabel
                                  htmlFor="optIn.consent"
                                  className="cursor-pointer font-roboto text-base font-normal"
                                >
                                  Aceito receber e-mails e comunicação da
                                  Harpio.
                                </FormLabel>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </>
                )}
                <div className="mt-4 grid gap-2">
                  <Button
                    className="cursor-pointer"
                    type="submit"
                    color="primary"
                    rounded="full"
                    disabled={
                      !confirmationForm.getValues("optIn.email") ||
                      !confirmationForm.getValues("optIn.consent") ||
                      confirmationForm.formState.isSubmitting ||
                      !isLoaded
                    }
                  >
                    {confirmationForm.formState.isSubmitting || !isLoaded ? (
                      <LoadingOutlined className="size-4" />
                    ) : (
                      "Confirmar"
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        )}
        {currentStep === "error" && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-center">
              <Image
                src="/imgs/server-error-illustration.svg"
                width={252}
                height={231}
                unoptimized
                alt="Algo deu errado"
                aria-hidden
              />
            </div>
            <div className="flex items-center justify-center">
              <span className="font-poppins text-2xl">Algo deu errado</span>
            </div>
            <div className="flex flex-row items-center justify-center">
              <p className="text-center text-base">
                Tente novamente ou envie um e-mail para o endereço a seguir:{" "}
                <Link
                  href="mailto: atendimento@harpio.com.br"
                  className="text-center text-base text-brand-primary underline"
                >
                  atendimento@harpio.com.br
                </Link>
                {value ? (
                  <span className="ml-2 text-brand-primary">
                    <CheckCircleOutlined /> Copiado
                  </span>
                ) : (
                  <CopyOutlined
                    className="ml-1 cursor-pointer text-brand-primary"
                    onClick={() => void copy("atendimento@harpio.com.br")}
                  />
                )}
              </p>
            </div>
            <div className="flex items-center justify-center">
              <Button
                className="cursor-pointer sm:px-12"
                type="button"
                color="primary"
                rounded="full"
                onClick={() => {
                  confirmationForm.reset({
                    optIn: {
                      consent: false,
                      email: false,
                    },
                  });
                  setCurrentStep("confirmation");
                }}
              >
                {confirmationForm.formState.isSubmitting ? (
                  <LoadingOutlined className="size-4" />
                ) : (
                  "Tentar novamente"
                )}
              </Button>
            </div>
          </div>
        )}
        {currentStep === "success" && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-center">
              <LottiePlayer
                autoplay
                // loop
                keepLastFrame
                src="/json/application-success.json"
                style={{ height: "206px", width: "206px" }}
              />
            </div>
            <div className="flex items-center justify-center">
              <span className="font-poppins text-2xl">Dados confirmados!</span>
            </div>
            <div className="flex items-center justify-center">
              <p className="text-center text-base">
                Você será direcionado em <strong>{timeLeft}</strong> segundo
                {timeLeft > 1 ? "s" : null}.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
}
